import {
  AddIcon,
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

export default function SectionEditor({
  sectionKey,
  section,
  onUpdate,
  onRename,
  onDelete,
  addConditionToSection,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(section?.MESSAGE || "");
  const [keyDraft, setKeyDraft] = useState(sectionKey);

  // Keep drafts in sync if parent updates
  useEffect(() => {
    if (!isEditing) {
      setLabel(section?.MESSAGE || "");
      setKeyDraft(sectionKey);
    }
  }, [sectionKey, section?.MESSAGE, isEditing]);

  const normalizeKey = (val) =>
    val
      .toUpperCase()
      .replace(/\s+/g, "_")
      .replace(/[^A-Z0-9_]/g, "");

  const isKeyInvalid = !keyDraft.trim();
  const isLabelInvalid = !label.trim();

  const isUnchanged = keyDraft === sectionKey && label === section?.MESSAGE;

  const canSave = !isKeyInvalid && !isLabelInvalid && !isUnchanged;

  const handleSave = () => {
    if (!canSave) return;

    const updatedSection = { ...section, MESSAGE: label };

    if (keyDraft !== sectionKey) {
      onRename(sectionKey, keyDraft, updatedSection);
    } else {
      onUpdate(sectionKey, updatedSection);
    }

    setIsEditing(false);
  };

  const handleCancel = () => {
    setKeyDraft(sectionKey);
    setLabel(section?.MESSAGE || "");
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <Box borderBottom="4px solid #edf2f7" p={3} mb={3}>
      <HStack justify="space-between" align="start">
        {isEditing ? (
          <HStack spacing={4} align="flex-end">
            <FormControl isInvalid={isKeyInvalid}>
              <FormLabel fontSize="xs" mb={1}>
                Field Id
              </FormLabel>
              <Input
                borderRadius="md"
                borderColor="gray.600"
                bg="white"
                placeholder="SECTION_KEY"
                value={keyDraft}
                onChange={(e) => setKeyDraft(normalizeKey(e.target.value))}
                onKeyDown={handleKeyDown}
              />
              {isKeyInvalid && (
                <FormErrorMessage fontSize="xs">
                  Field Id is required
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl isInvalid={isLabelInvalid}>
              <FormLabel fontSize="xs" mb={1}>
                Field Name
              </FormLabel>
              <Input
                borderRadius="md"
                borderColor="gray.600"
                bg="white"
                placeholder="Section Label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {isLabelInvalid && (
                <FormErrorMessage fontSize="xs">
                  Field Name is required
                </FormErrorMessage>
              )}
            </FormControl>
          </HStack>
        ) : (
          <Text fontWeight="bold">
            {sectionKey} — {section?.MESSAGE}
          </Text>
        )}

        <HStack spacing={4}>
          <Button
            onClick={() => addConditionToSection(sectionKey)}
            size="sm"
            leftIcon={<AddIcon />}
          >
            Add Condition
          </Button>

          {isEditing ? (
            <>
              <CheckIcon
                cursor={canSave ? "pointer" : "not-allowed"}
                opacity={canSave ? 1 : 0.4}
                onClick={handleSave}
              />
              <CloseIcon cursor="pointer" onClick={handleCancel} />
            </>
          ) : (
            <EditIcon cursor="pointer" onClick={() => setIsEditing(true)} />
          )}

          <DeleteIcon
            cursor="pointer"
            color="red"
            onClick={() => onDelete(sectionKey)}
          />
        </HStack>
      </HStack>
    </Box>
  );
}
